import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import queryString, { ParsedQuery } from 'query-string'
import isString from 'lodash/isString'
import isEqual from 'lodash/isEqual'
import { FilterParam } from '../IndexPage/FilterBar/FilterBar.types'
import { getPaginationLink } from './paginate.helpers'

export const usePageSizeFromUrl = (defaultPageSize: number): number => {
  const [searchParams] = useSearchParams()
  const raw = Number(searchParams.get('pc'))
  return raw > 0 ? raw : defaultPageSize
}

const normalizeBracketedKey = (key: string): string =>
  key.replace(/(\[\])+$/g, '')

const normalizeParsedQuery = (
  query: ParsedQuery<string>,
): ParsedQuery<string> => {
  const normalized: ParsedQuery<string> = {}
  const toArray = (value: string | string[] | null | undefined): string[] => {
    if (value === undefined || value === null) return []
    if (Array.isArray(value)) return value
    return [value]
  }

  Object.entries(query).forEach(([rawKey, rawValue]) => {
    const key = normalizeBracketedKey(rawKey)
    const mergedValues = [...toArray(normalized[key]), ...toArray(rawValue)]
    const deduped = Array.from(new Set(mergedValues))
    if (deduped.length === 0) return
    normalized[key] = deduped.length === 1 ? deduped[0] : deduped
  })

  return normalized
}

export type PaginatedItemsType<T> = {
  paginatedItems: T[]
  currentPage: number
  totalPages: number
  nextPage: () => void
  prevPage: () => void
  goToPage: (page: number) => void
}

export type PaginatedItemsInputType<T> = {
  items: T[]
  pageSize: number
  initialPage: number
  onNext?: (currentPage: number, totalPages: number) => void
  onPrev?: (currentPage: number) => void
  onGoToPage?: (page: number, totalPages: number) => void
}

/**
 * This hook is used to paginate an array of items
 * @param items - The array of items to paginate
 * @param pageSize - The number of items per page
 * @returns paginatedItems - The paginated items
 * @returns currentPage - The current page
 * @returns totalPages - The total number of pages
 */
export const usePaginateItems = <T,>({
  items,
  pageSize,
  initialPage,
  onNext,
  onPrev,
  onGoToPage,
}: PaginatedItemsInputType<T>): PaginatedItemsType<T> => {
  const [currentPage, setCurrentPage] = useState(initialPage)

  const totalPages = useMemo(
    () => Math.ceil(items.length / pageSize),
    [items, pageSize],
  )

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return items.slice(start, start + pageSize)
  }, [items, currentPage, pageSize])

  const nextPage = useCallback(() => {
    if (onNext) {
      onNext(currentPage, totalPages)
    } else {
      setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev))
    }
  }, [totalPages, onNext, currentPage])

  const prevPage = useCallback(() => {
    if (onPrev) {
      onPrev(currentPage)
    } else {
      setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev))
    }
  }, [onPrev, currentPage])

  const goToPage = useCallback(
    (page: number) => {
      onGoToPage?.(page, totalPages)
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page)
      }
    },
    [totalPages, onGoToPage],
  )

  useEffect(() => {
    goToPage(initialPage)
  }, [initialPage, goToPage])

  return useMemo(
    () => ({
      paginatedItems,
      currentPage,
      totalPages,
      nextPage,
      prevPage,
      goToPage,
    }),
    [paginatedItems, currentPage, totalPages, nextPage, prevPage, goToPage],
  )
}

export const usePaginateItemsWithNavigate = <T,>({
  items,
  pageSize,
}: Omit<PaginatedItemsInputType<T>, 'initialPage'>): PaginatedItemsType<T> => {
  const location = useLocation()
  const navigate = useNavigate()
  const prevParams = useRef<ParsedQuery<string>>({})
  const searchParams = useMemo(
    () =>
      normalizeParsedQuery(
        queryString.parse(location.search, {
          arrayFormat: 'bracket',
        }),
      ),
    [location],
  )
  const initialPage = useMemo(() => {
    const pageParam = searchParams[FilterParam.PAGE]
    return isString(pageParam) ? Number(pageParam) : 1
  }, [searchParams])

  useEffect(() => {
    if (prevParams.current && Object.keys(prevParams.current).length === 0) {
      prevParams.current = searchParams
      return
    }

    // get params minus page param
    const prevParamsCopy = { ...prevParams.current }
    const paramsCopy = { ...searchParams }

    delete prevParamsCopy[FilterParam.PAGE]
    delete paramsCopy[FilterParam.PAGE]

    const filtersChanged = !isEqual(paramsCopy, prevParamsCopy)

    prevParams.current = searchParams

    // Only reset when filters/search actually changed AND we are not already on page 1
    const pageRaw = searchParams[FilterParam.PAGE]
    const page = isString(pageRaw)
      ? Number(pageRaw)
      : Array.isArray(pageRaw)
      ? Number(pageRaw[0])
      : 1

    if (filtersChanged && page > 1) {
      const url = queryString.stringifyUrl(
        {
          url: location.pathname,
          query: {
            ...paramsCopy,
            [FilterParam.PAGE]: 1,
          },
        },
        {
          arrayFormat: 'bracket',
        },
      )
      navigate(url, { replace: true }) // Use replace to avoid redundant history entries
    }
  }, [searchParams, location.pathname, navigate, prevParams])

  const onNext = useCallback(
    (currentPage: number, totalPages: number) => {
      if (currentPage >= totalPages) return
      const link = getPaginationLink(
        location.pathname,
        currentPage + 1,
        searchParams,
      )
      navigate(link)
    },
    [navigate, location.pathname, searchParams],
  )

  const onPrev = useCallback(
    (currentPage: number) => {
      if (currentPage <= 1) return
      navigate(
        getPaginationLink(location.pathname, currentPage - 1, searchParams),
      )
    },
    [navigate, location.pathname, searchParams],
  )

  const result = usePaginateItems({
    items,
    pageSize,
    initialPage,
    onNext,
    onPrev,
  })

  useEffect(() => {
    if (result.totalPages > 0 && result.currentPage > result.totalPages) {
      navigate(
        getPaginationLink(location.pathname, result.totalPages, searchParams),
        { replace: true },
      )
    }
  }, [
    result.currentPage,
    result.totalPages,
    navigate,
    location.pathname,
    searchParams,
  ])

  return result
}
